import { graphql } from 'gatsby';
import React from 'react';
import { css } from '@emotion/react';
import { colors } from '../../primitives/colors';
import Container from '../../primitives/grid/container';
import paddingDefault from '../../utils/padding-default';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import ArticleCard from '../../resources/article-card';
import { breakpoints } from '../../primitives/tokens';
import { ArticleResult } from '../../resources/types';

const styles = {
    container: css({
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    }),
    blogrollHeading: css({
        marginBottom: '40px',
        fontSize: '24px',
        lineHeight: '32px',
        color: colors.primaryOrange,
        borderColor: colors.primaryOrange,
        paddingBottom: '8px',
        paddingTop: '16px',
        borderBottomWidth: '1px',
        fontWeight: 500,
        [breakpoints.tb]: {
            marginBottom: '8px',
            fontSize: '18px',
            lineHeight: '28px',
        },
    }),
    blogrollGridWrapper: (isFull: boolean) => css({
        marginBottom: '40px',
        width: isFull ? '100%' : '49%',

        [breakpoints.tb]: {
            marginBottom: '8px',
            width: '100%',

        },
    }),
    blogrollGrid: (isFull: boolean) => css({
        gridTemplateColumns: isFull ? 'repeat(4, minmax(0, 1fr))' : undefined,
        rowGap: '64px',
        display: 'grid',
        paddingTop: '16px',
        paddingBottom: '40px',
        gap: '24px',
        [breakpoints.tb]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        [breakpoints.mb]: {
            gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
        },
    }),
    blogrollGridItem: css({
        flex: '1 1 auto',
        width: '100%',
    }),
};

const MultiBlogroll: React.FC<Queries.MultiBlogrollDataFragment> = (data) => {
    const { subblog: subblogs } = data;

    if (!subblogs) return null;

    return (
        <section css={paddingDefault()}>
            <Container css={styles.container}>
                {!!subblogs && subblogs.map((subroll) => {
                    if (!subroll || !subroll.articles) return null;
                    if (!subroll.articles.articles) return null;
                    const { title, articles } = subroll.articles;
                    const isFull = articles.length === 4;

                    return (
                        <div css={styles.blogrollGridWrapper(isFull)} key={title}>
                            {title && (
                                <h2 css={styles.blogrollHeading}>
                                    {apCaseOnlyTitleTags(title)}
                                </h2>
                            )}
                            <ul css={styles.blogrollGrid(isFull)}>
                                {articles.map((article) => {
                                    if (!article || !article.title || !article.url || !article.content) return null;

                                    const item: ArticleResult = {
                                        title: article.title,
                                        url: article.url,
                                        featuredImage: article.featuredImage ?? undefined,
                                        content: article.content,
                                    };

                                    return (
                                        <li css={styles.blogrollGridItem} key={article.title}>
                                            <ArticleCard article={item} />
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    );
                })}
            </Container>
        </section>
    );
};

export const query = graphql`
  fragment MultiBlogrollData on CsMultiBlogroll {
    ... on CsMultiBlogroll {
      subblog {
        heading
        articles {
          title
          articles {
            featuredImage {
              filename
              title
              description
              url
              contentStackAsset(width: 1440)
            }
            title
            content
            url
          }
        }
      }
    }
  }
`;

export default MultiBlogroll;
